/**
 * Axis-aligned bounding boxes + swept voxel collision helpers.
 * Includes optional auto-step for ledges up to maxStepHeight blocks.
 */

export interface Aabb {
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
}

export function aabbFromCenter(
  cx: number,
  cy: number,
  cz: number,
  halfW: number,
  height: number,
): Aabb {
  return {
    minX: cx - halfW,
    maxX: cx + halfW,
    minY: cy,
    maxY: cy + height,
    minZ: cz - halfW,
    maxZ: cz + halfW,
  };
}

export function expandAabb(a: Aabb, dx: number, dy: number, dz: number): Aabb {
  return {
    minX: a.minX + Math.min(0, dx),
    maxX: a.maxX + Math.max(0, dx),
    minY: a.minY + Math.min(0, dy),
    maxY: a.maxY + Math.max(0, dy),
    minZ: a.minZ + Math.min(0, dz),
    maxZ: a.maxZ + Math.max(0, dz),
  };
}

export function overlaps(a: Aabb, b: Aabb): boolean {
  return (
    a.minX < b.maxX &&
    a.maxX > b.minX &&
    a.minY < b.maxY &&
    a.maxY > b.minY &&
    a.minZ < b.maxZ &&
    a.maxZ > b.minZ
  );
}

/** Block solid as unit cube [x,x+1) etc. */
export function blockAabb(bx: number, by: number, bz: number): Aabb {
  return {
    minX: bx,
    maxX: bx + 1,
    minY: by,
    maxY: by + 1,
    minZ: bz,
    maxZ: bz + 1,
  };
}

export interface MoveCollideOptions {
  /**
   * Max automatic step-up height in world/block units.
   * Ledges ≤ this are walked; taller remain walls. Default 0 (off).
   */
  maxStepHeight?: number;
}

/**
 * Move AABB by (dx,dy,dz) resolving against solid sampler.
 * Returns final delta and grounded flag.
 */
export function moveAndCollide(
  box: Aabb,
  dx: number,
  dy: number,
  dz: number,
  isSolidAt: (x: number, y: number, z: number) => boolean,
  opts: MoveCollideOptions = {},
): { dx: number; dy: number; dz: number; grounded: boolean } {
  const maxStep = Math.max(0, opts.maxStepHeight ?? 0);
  const startMinY = box.minY;

  const movedY = resolveY(box, dy, isSolidAt);
  box.minY += movedY;
  box.maxY += movedY;

  let movedX = resolveX(box, dx, isSolidAt);
  if (maxStep > 0 && dx !== 0 && Math.abs(movedX - dx) > 1e-8) {
    movedX = tryStepAxis(box, "x", dx, movedX, maxStep, isSolidAt);
  }
  box.minX += movedX;
  box.maxX += movedX;

  let movedZ = resolveZ(box, dz, isSolidAt);
  if (maxStep > 0 && dz !== 0 && Math.abs(movedZ - dz) > 1e-8) {
    movedZ = tryStepAxis(box, "z", dz, movedZ, maxStep, isSolidAt);
  }
  box.minZ += movedZ;
  box.maxZ += movedZ;

  // Grounded probe
  const feet = {
    minX: box.minX + 0.01,
    maxX: box.maxX - 0.01,
    minY: box.minY - 0.08,
    maxY: box.minY - 0.01,
    minZ: box.minZ + 0.01,
    maxZ: box.maxZ - 0.01,
  };
  let grounded = false;
  for (const [bx, by, bz] of collectBlocks(feet)) {
    if (isSolidAt(bx, by, bz) && overlaps(feet, blockAabb(bx, by, bz))) {
      grounded = true;
      break;
    }
  }

  // Report total Y delta from this call (includes step-up lift)
  const totalDy = box.minY - startMinY;
  return { dx: movedX, dy: totalDy, dz: movedZ, grounded };
}

/** Attempt step-up when a horizontal axis is blocked. Mutates box Y if successful. */
function tryStepAxis(
  box: Aabb,
  axis: "x" | "z",
  wish: number,
  blocked: number,
  maxStep: number,
  isSolidAt: (x: number, y: number, z: number) => boolean,
): number {
  // Snapshot horizontal (Y may be adjusted during probe)
  const snap = {
    minX: box.minX,
    maxX: box.maxX,
    minY: box.minY,
    maxY: box.maxY,
    minZ: box.minZ,
    maxZ: box.maxZ,
  };

  // Need headroom to rise
  const up = resolveY(box, maxStep, isSolidAt);
  if (up < 0.45) {
    // restore (resolveY does not mutate)
    return blocked;
  }

  box.minY = snap.minY + up;
  box.maxY = snap.maxY + up;

  const stepped =
    axis === "x"
      ? resolveX(box, wish, isSolidAt)
      : resolveZ(box, wish, isSolidAt);

  // Must make meaningful progress vs blocked slide
  if (Math.abs(stepped) <= Math.abs(blocked) + 1e-6) {
    box.minY = snap.minY;
    box.maxY = snap.maxY;
    return blocked;
  }

  // Apply horizontal in probe space then settle down onto the ledge
  if (axis === "x") {
    box.minX = snap.minX + stepped;
    box.maxX = snap.maxX + stepped;
    box.minZ = snap.minZ;
    box.maxZ = snap.maxZ;
  } else {
    box.minZ = snap.minZ + stepped;
    box.maxZ = snap.maxZ + stepped;
    box.minX = snap.minX;
    box.maxX = snap.maxX;
  }

  const down = resolveY(box, -(up + 0.25), isSolidAt);
  box.minY += down;
  box.maxY += down;

  // Keep step only if we didn't end below start (failed ledge) and rise ≤ maxStep
  const rise = box.minY - snap.minY;
  if (rise < -1e-3 || rise > maxStep + 0.05) {
    box.minX = snap.minX;
    box.maxX = snap.maxX;
    box.minY = snap.minY;
    box.maxY = snap.maxY;
    box.minZ = snap.minZ;
    box.maxZ = snap.maxZ;
    return blocked;
  }

  // Caller applies horizontal from original; Y already final on box.
  // Reset XZ to snap so caller `box.minX += movedX` stays correct; keep Y.
  const finalYMin = box.minY;
  const finalYMax = box.maxY;
  box.minX = snap.minX;
  box.maxX = snap.maxX;
  box.minZ = snap.minZ;
  box.maxZ = snap.maxZ;
  box.minY = finalYMin;
  box.maxY = finalYMax;
  return stepped;
}

function resolveY(
  box: Aabb,
  movedY: number,
  isSolidAt: (x: number, y: number, z: number) => boolean,
): number {
  if (movedY === 0) return 0;
  let my = movedY;
  const blocks = collectBlocks(expandAabb(box, 0, my, 0));
  for (const [bx, by, bz] of blocks) {
    if (!isSolidAt(bx, by, bz)) continue;
    const b = blockAabb(bx, by, bz);
    if (!overlaps({ ...box, minY: box.minY + my, maxY: box.maxY + my }, b)) {
      continue;
    }
    if (my > 0) my = b.minY - box.maxY - 1e-5;
    else my = b.maxY - box.minY + 1e-5;
    if (Math.abs(my) < 1e-8) my = 0;
  }
  return my;
}

function resolveX(
  box: Aabb,
  movedX: number,
  isSolidAt: (x: number, y: number, z: number) => boolean,
): number {
  if (movedX === 0) return 0;
  let mx = movedX;
  const blocks = collectBlocks(expandAabb(box, mx, 0, 0));
  for (const [bx, by, bz] of blocks) {
    if (!isSolidAt(bx, by, bz)) continue;
    const b = blockAabb(bx, by, bz);
    if (!overlaps({ ...box, minX: box.minX + mx, maxX: box.maxX + mx }, b)) {
      continue;
    }
    if (mx > 0) mx = b.minX - box.maxX - 1e-5;
    else mx = b.maxX - box.minX + 1e-5;
    if (Math.abs(mx) < 1e-8) mx = 0;
  }
  return mx;
}

function resolveZ(
  box: Aabb,
  movedZ: number,
  isSolidAt: (x: number, y: number, z: number) => boolean,
): number {
  if (movedZ === 0) return 0;
  let mz = movedZ;
  const blocks = collectBlocks(expandAabb(box, 0, 0, mz));
  for (const [bx, by, bz] of blocks) {
    if (!isSolidAt(bx, by, bz)) continue;
    const b = blockAabb(bx, by, bz);
    if (!overlaps({ ...box, minZ: box.minZ + mz, maxZ: box.maxZ + mz }, b)) {
      continue;
    }
    if (mz > 0) mz = b.minZ - box.maxZ - 1e-5;
    else mz = b.maxZ - box.minZ + 1e-5;
    if (Math.abs(mz) < 1e-8) mz = 0;
  }
  return mz;
}

function collectBlocks(a: Aabb): Array<[number, number, number]> {
  const out: Array<[number, number, number]> = [];
  const minX = Math.floor(a.minX);
  const maxX = Math.floor(a.maxX);
  const minY = Math.floor(a.minY);
  const maxY = Math.floor(a.maxY);
  const minZ = Math.floor(a.minZ);
  const maxZ = Math.floor(a.maxZ);
  for (let y = minY; y <= maxY; y++) {
    for (let z = minZ; z <= maxZ; z++) {
      for (let x = minX; x <= maxX; x++) {
        out.push([x, y, z]);
      }
    }
  }
  return out;
}
